import type { Artifact, ArtifactCategory, ArtifactClaimMode, ArtifactType } from '@/config/objects/artifacts';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  FormFieldGroup,
  FormPageLayout,
  ImageUploadDropzone,
  Input,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface ArtifactFormData {
  name: string;
  artifactType: ArtifactType;
  artifactCategory: ArtifactCategory;
  description: string;
  detectionRadius: number;
  claimRadius: number;
  effectDuration: string;
  effectValue: string;
  imageId: string;
  gateEnabled: boolean;
  gateClue: string;
  gateAnswer: string;
  targetCount: number;
  claimMode: ArtifactClaimMode;
}

const DEFAULT_FORM: ArtifactFormData = {
  name: '',
  artifactType: 'shield',
  artifactCategory: 'defensive',
  description: '',
  detectionRadius: 100,
  claimRadius: 10,
  effectDuration: '',
  effectValue: '',
  imageId: '',
  gateEnabled: false,
  gateClue: '',
  gateAnswer: '',
  targetCount: 1,
  claimMode: 'all_teams',
};

// Map artifact type to its category
const typeCategoryMap: Record<ArtifactType, ArtifactCategory> = {
  scramble: 'offensive',
  freeze: 'offensive',
  blackout: 'offensive',
  steal: 'offensive',
  misdirect: 'offensive',
  shield: 'defensive',
  double_down: 'wildcard',
};

export function ArtifactFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const [formData, setFormData] = useState<ArtifactFormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [formReady, setFormReady] = useState(!isEdit);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  const updateField = useCallback(
    <K extends keyof ArtifactFormData>(key: K, value: ArtifactFormData[K]) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Auto-update category when type changes
  const handleTypeChange = useCallback(
    (type: ArtifactType) => {
      updateField('artifactType', type);
      updateField('artifactCategory', typeCategoryMap[type]);
    },
    [updateField],
  );

  // Load existing artifact (edit mode)
  const {
    data: artifactResponse,
    isLoading,
    isError,
  } = useQuery<{ success: boolean; data: Artifact }>({
    queryKey: ['Artifact', itemId],
    queryFn: () => apiClient.get(`/artifacts/${itemId}`),
    enabled: !!itemId,
  });

  useEffect(() => {
    if (!artifactResponse?.data) return;
    const a = artifactResponse.data;
    setFormData({
      name: a.name ?? '',
      artifactType: a.artifactType ?? 'shield',
      artifactCategory: a.artifactCategory ?? 'defensive',
      description: a.description ?? '',
      detectionRadius: a.detectionRadius ?? 100,
      claimRadius: a.claimRadius ?? 10,
      effectDuration: a.effectDuration?.toString() ?? '',
      effectValue: a.effectValue?.toString() ?? '',
      imageId: a.imageUrl ?? '',
      gateEnabled: a.gateEnabled ?? false,
      gateClue: a.gateClue ?? '',
      gateAnswer: a.gateAnswer ?? '',
      targetCount: a.targetCount ?? 1,
      claimMode: a.claimMode ?? 'all_teams',
    });
    setLastSaved(new Date(a.updatedAt));
    setFormReady(true);
  }, [artifactResponse]);

  useEffect(() => {
    if (isError) {
      toast.error('Artifact not found');
      navigate('/artifacts');
    }
  }, [isError, navigate]);

  // Validation
  const validateForm = (): boolean => {
    const errs: Record<string, string> = {};

    if (!formData.name.trim()) errs.name = 'Name is required';
    if (formData.detectionRadius <= 0) errs.detectionRadius = 'Must be greater than 0';
    if (formData.claimRadius <= 0) errs.claimRadius = 'Must be greater than 0';
    if (formData.claimRadius > formData.detectionRadius) {
      errs.claimRadius = 'Claim radius must be less than or equal to detection radius';
    }
    if (formData.gateEnabled && !formData.gateAnswer.trim()) {
      errs.gateAnswer = 'Gate answer is required when gate is enabled';
    }

    setErrors(errs);

    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.getElementById(`field-${firstKey}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  // Prepare payload
  const preparePayload = () => ({
    name: formData.name.trim(),
    artifactType: formData.artifactType,
    artifactCategory: formData.artifactCategory,
    description: formData.description.trim() || null,
    detectionRadius: formData.detectionRadius,
    claimRadius: formData.claimRadius,
    effectDuration: formData.effectDuration ? parseInt(formData.effectDuration) : null,
    effectValue: formData.effectValue ? parseFloat(formData.effectValue) : null,
    imageUrl: formData.imageId || null,
    gateEnabled: formData.gateEnabled,
    gateClue: formData.gateEnabled ? formData.gateClue.trim() || null : null,
    gateAnswer: formData.gateEnabled ? formData.gateAnswer.trim() || null : null,
    targetCount: formData.targetCount,
    claimMode: formData.claimMode,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return apiClient.put<{ data: Artifact }>(`/artifacts/${itemId}`, payload);
      }
      return apiClient.post<{ data: Artifact }>('/artifacts', payload);
    },
    onSuccess: (response: { data: Artifact }) => {
      queryClient.invalidateQueries({ queryKey: ['Artifact'] });
      setLastSaved(new Date());
      if (isEdit) {
        toast.success('Artifact saved successfully');
        navigate(`/artifacts/${itemId}`);
      } else {
        toast.success('Artifact created successfully');
        navigate(`/artifacts/${response.data.id}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to save artifact');
    },
  });

  const handleSave = () => {
    if (!validateForm()) return;
    saveMutation.mutate(preparePayload());
  };

  const handleCancel = () => {
    if (isEdit) {
      navigate(`/artifacts/${itemId}`);
    } else {
      navigate('/artifacts');
    }
  };

  // Loading skeleton
  if (isEdit && (isLoading || !formReady)) {
    return (
      <FormPageLayout>
        <FormPageLayout.Header>
          <div className="flex items-center gap-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-20" />
            <Skeleton className="h-9 w-32" />
          </div>
        </FormPageLayout.Header>
        <FormPageLayout.Content>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="rounded-xl border bg-card shadow-sm">
              <CardHeader className="px-6 py-4 !pb-4 border-b">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </FormPageLayout.Content>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout>
      {/* Header */}
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleCancel}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {isEdit ? 'Edit Artifact' : 'Create Artifact'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEdit ? formData.name || 'Update artifact details' : 'Add a new artifact to the library'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Header>

      {/* Content */}
      <FormPageLayout.Content>
        <div ref={firstErrorRef} className="space-y-6">
          {/* Artifact Details */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Artifact Details</CardTitle>
              <CardDescription>Basic information about the artifact</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormFieldGroup name="name" label="Name" required error={errors.name}>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Enter artifact name"
                  className={cn(errors.name && 'border-destructive')}
                />
              </FormFieldGroup>

              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup name="artifactType" label="Type" required>
                  <Select value={formData.artifactType} onValueChange={(v) => handleTypeChange(v as ArtifactType)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scramble">Scramble</SelectItem>
                      <SelectItem value="freeze">Freeze</SelectItem>
                      <SelectItem value="blackout">Blackout</SelectItem>
                      <SelectItem value="steal">Steal</SelectItem>
                      <SelectItem value="misdirect">Misdirect</SelectItem>
                      <SelectItem value="shield">Shield</SelectItem>
                      <SelectItem value="double_down">Double Down</SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldGroup>

                <FormFieldGroup name="artifactCategory" label="Category" required>
                  <Select value={formData.artifactCategory} onValueChange={(v) => updateField('artifactCategory', v as ArtifactCategory)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="offensive">Offensive</SelectItem>
                      <SelectItem value="defensive">Defensive</SelectItem>
                      <SelectItem value="wildcard">Wildcard</SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldGroup>
              </div>

              <FormFieldGroup name="description" label="Description">
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => updateField('description', value ?? '')}
                />
              </FormFieldGroup>
            </CardContent>
          </Card>

          {/* Discovery & Effect */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Discovery & Effect</CardTitle>
              <CardDescription>How teams find and use this artifact</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup
                  name="detectionRadius"
                  label="Detection Radius (m)"
                  description="Distance when teams get an alert"
                  required
                  error={errors.detectionRadius}
                >
                  <Input
                    id="detectionRadius"
                    type="number"
                    value={formData.detectionRadius}
                    onChange={(e) => updateField('detectionRadius', parseInt(e.target.value) || 0)}
                    placeholder="100"
                  />
                </FormFieldGroup>

                <FormFieldGroup
                  name="claimRadius"
                  label="Claim Radius (m)"
                  description="Distance when teams can claim"
                  required
                  error={errors.claimRadius}
                >
                  <Input
                    id="claimRadius"
                    type="number"
                    value={formData.claimRadius}
                    onChange={(e) => updateField('claimRadius', parseInt(e.target.value) || 0)}
                    placeholder="10"
                  />
                </FormFieldGroup>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup
                  name="effectDuration"
                  label="Effect Duration (min)"
                  description="Leave empty for non-timed effects"
                >
                  <Input
                    id="effectDuration"
                    type="number"
                    value={formData.effectDuration}
                    onChange={(e) => updateField('effectDuration', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </FormFieldGroup>

                <FormFieldGroup
                  name="effectValue"
                  label="Effect Value"
                  description="Points for steal, multiplier value, etc."
                >
                  <Input
                    id="effectValue"
                    type="number"
                    step="any"
                    value={formData.effectValue}
                    onChange={(e) => updateField('effectValue', e.target.value)}
                    placeholder="e.g., 50"
                  />
                </FormFieldGroup>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <FormFieldGroup
                  name="targetCount"
                  label="Target Count"
                  description="Number of teams to target (0 = all teams)"
                >
                  <Input
                    id="targetCount"
                    type="number"
                    min={0}
                    value={formData.targetCount}
                    onChange={(e) => updateField('targetCount', parseInt(e.target.value) || 0)}
                    placeholder="1"
                  />
                </FormFieldGroup>

                <FormFieldGroup name="claimMode" label="Claim Mode" required>
                  <Select value={formData.claimMode} onValueChange={(v) => updateField('claimMode', v as ArtifactClaimMode)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select claim mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_teams">All Teams</SelectItem>
                      <SelectItem value="first_claim">First Claim Only</SelectItem>
                    </SelectContent>
                  </Select>
                </FormFieldGroup>
              </div>
            </CardContent>
          </Card>

          {/* Gate Challenge */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Gate Challenge</CardTitle>
              <CardDescription>Optional question teams must answer before claiming</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="gateEnabled"
                  checked={formData.gateEnabled}
                  onCheckedChange={(checked) => updateField('gateEnabled', !!checked)}
                />
                <label htmlFor="gateEnabled" className="text-sm font-medium cursor-pointer">
                  Require answering a question before claiming
                </label>
              </div>

              {formData.gateEnabled && (
                <>
                  <FormFieldGroup name="gateClue" label="Gate Clue">
                    <RichTextEditor
                      value={formData.gateClue}
                      onChange={(value) => updateField('gateClue', value ?? '')}
                    />
                  </FormFieldGroup>

                  <FormFieldGroup name="gateAnswer" label="Gate Answer" required error={errors.gateAnswer}>
                    <Input
                      id="gateAnswer"
                      value={formData.gateAnswer}
                      onChange={(e) => updateField('gateAnswer', e.target.value)}
                      placeholder="Expected answer"
                      className={cn(errors.gateAnswer && 'border-destructive')}
                    />
                  </FormFieldGroup>
                </>
              )}
            </CardContent>
          </Card>

          {/* Media */}
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
            <CardHeader className="px-6 py-4 !pb-4 border-b">
              <CardTitle>Media</CardTitle>
              <CardDescription>Visual for the artifact</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <ImageUploadDropzone targetAspectRatio={4/3}
                label="Artifact Image"
                value={formData.imageId || undefined}
                onChange={(mediaId) => updateField('imageId', mediaId || '')}
              />
            </CardContent>
          </Card>
        </div>
      </FormPageLayout.Content>

      {/* Footer */}
      <FormPageLayout.Footer lastSaved={lastSaved}>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={saveMutation.isPending} icon={Save}>
            Save Changes
          </Button>
        </div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
